import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react'

import {
  Alert,
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  HStack,
  Icon,
  Menu,
  Portal,
  Select,
  Spacer,
  Span,
  Spinner,
  Switch,
  Text,
  VStack,
  createListCollection,
  defineStyle,
  useDisclosure,
} from '@chakra-ui/react'
import { CAD2DIcon, CAD3DIcon, DownloadIcon, PCDAlignmentIcon, SettingIcon } from '@editor/assets/icons'
import { CAD_FILE_TYPES, MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { CADFileType, InspectionArea, InspectionItemType } from '@editor/interfaces/interfaces'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { collectConnectedAreas } from '@editor/pages/projects/pcd-alignment/utils'
import { downloadCadFile } from '@editor/services/AWS'
import { getSignedUrlForCadFile, post2dCadFiles, postCadFiles } from '@editor/services/InspectionArea'
import { shapesExist, withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { Tooltip } from '@infraloop/common'
import fileDownload from 'js-file-download'
import { useSelector } from 'react-redux'
import { Matrix4, Vector3 } from 'three'

enum ExportType {
  CAD3D,
  CAD2D,
}

const cadFileTypeOptions = createListCollection({
  items: Object.values(CAD_FILE_TYPES).map((value: CADFileType) => ({ label: `.${value}`, value })),
})

/** Pill listing an area this one is aligned with. */
const connectedAreaLabelStyle = defineStyle({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  mt: 1,
  mr: 1,
  px: 1,
  py: '0.1rem',
  fontSize: '0.9rem',
  color: 'primary.400',
  backgroundColor: 'primary.50',
  borderWidth: 1,
  borderColor: 'primary.100',
  borderRadius: 5,
})

/**
 * One half of the 出力次元 segmented control. Both halves carry the same two-tone
 * gradient at twice their width, so selecting one slides its fill in from the divider
 * rather than snapping. The caller supplies the gradient direction and the position.
 */
const dimensionButtonStyle = defineStyle({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  width: '100%',
  height: 10,
  px: 4,
  fontSize: 'md',
  cursor: 'pointer',
  backgroundSize: '200%',
  transition: 'color 0.3s ease-in-out, background-position 0.3s',
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
})

/**
 * Styling for one half of the segmented control. `Tooltip` renders its child bare when it
 * has no label and wraps it in a plain div when it does, so the nested rule keeps both
 * shapes filling the half.
 */
const dimensionSlotProps = {
  flex: 1,
  css: { '& > div': { display: 'flex', width: '100%' } },
}

const CADExportButton: FC = () => {
  const { open, onOpen, onClose } = useDisclosure()

  // Ref
  const modalBodyRef = useRef<HTMLDivElement>(null)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const cameraProfile = useSelector((state: RootState) => state.toolCameraProfile.cameraProfile)
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)

  // Context
  const { showModal, showErrorModal } = useContext(GlobalModalContext)
  const { getAccessToken } = useContext(UserContext)

  //* ダウンロード処理用
  const [extension, setExtension] = useState<CADFileType>(CAD_FILE_TYPES.ifc)
  const [isExporting3D, setIsExporting3D] = useState(false)
  const [isExporting2D, setIsExporting2D] = useState(false)
  const [progress3D, setProgress3D] = useState(0)
  const [progress2D, setProgress2D] = useState(0)
  const [exportType, setExportType] = useState<ExportType>(isExporting3D ? ExportType.CAD2D : ExportType.CAD3D)
  const [includeConnectedAreas, setIncludeConnectedAreas] = useState(false)

  // Derived
  const isExporting = isExporting2D || isExporting3D // || true
  const cad2dError = useMemo(() => {
    if (
      !shapes?.polygons?.length &&
      !inspectionItems?.some((item) => item.item_type === InspectionItemType.PolylineLength)
    ) {
      return '平面、延長の要素がありません'
    }

    if (!cameraProfile) {
      return '先にカメラの正面を設定してください'
    }

    return null
  }, [shapes, cameraProfile, inspectionItems])

  const cad3dError = useMemo(() => {
    if (!shapesExist(shapes)) {
      return '鉄筋、平面等の要素がありません'
    }

    return null
  }, [shapes])

  /**
   * Generate connected areas connections.
   *
   * @param ar The inspection area to generate connections for.
   */
  const connectedAreas = useMemo(() => {
    if (!inspectionArea) return []

    return collectConnectedAreas(
      {
        current: {
          area: inspectionArea,
          anchors: [],
          center: new Vector3(),
        },
        matrix: new Matrix4().identity(),
        inverted: false,
      },
      inspectionArea,
      inspectionAreas,
    )
  }, [inspectionArea, inspectionAreas])

  /**
   * Reset the modal state.
   */
  const reset = useCallback(() => {
    setProgress3D(0)
    if (!isExporting3D && !isExporting2D) {
      dispatch(setAttentionText({ message: '' }))
    }
  }, [dispatch, isExporting2D, isExporting3D])

  //* ダウンロード処理
  const download = () => {
    if (!project?.project_id || !inspectionArea?.inspection_area_id) {
      showModal({
        body: '工事が存在しません。',
        modalType: MODAL_TYPES.ERROR,
      })
      return
    }

    dispatch(
      setAttentionText({
        message: 'CADファイルを生成中です。しばらくお待ちください。',
      }),
    )

    const tempExportType = exportType
    void withPromiseContained(async () => {
      const token = await getAccessToken()
      if (!token) return

      let generatedCad: InspectionArea | null = null
      let setProgress: React.Dispatch<React.SetStateAction<number>> | null = null
      if (tempExportType === ExportType.CAD2D) {
        setProgress = setProgress2D
        setIsExporting2D(true)
        // prettier-ignore
        generatedCad = await post2dCadFiles(
          token,
          project.project_id,
          inspectionArea.inspection_area_id,
          includeConnectedAreas,
          showErrorModal,
        )
      } else {
        setProgress = setProgress3D
        setIsExporting3D(true)
        generatedCad = await postCadFiles(
          token,
          project.project_id,
          inspectionArea.inspection_area_id,
          extension,
          includeConnectedAreas,
          showErrorModal,
        )
      }

      // if fails, reset and return.
      // note that the error message is shown in the showErrorModal function.
      if (!generatedCad) {
        return
      }

      const { cad_file, cad_file_2d } = generatedCad
      const generatedFileName = tempExportType === ExportType.CAD2D ? cad_file_2d?.name : cad_file?.name

      if (!generatedFileName) {
        showModal({
          body: 'データが存在しません。',
          modalType: MODAL_TYPES.ERROR,
        })
        return
      }

      //* cadファイル用の署名付きURL取得
      const downloadUrl = await getSignedUrlForCadFile(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        showErrorModal,
        tempExportType === ExportType.CAD2D ? '2D' : '3D',
      )
      if (!downloadUrl) return

      //* 署名付きURLからcadファイルをダウンロード
      const downloadResult = await downloadCadFile(downloadUrl, setProgress, showErrorModal)
      if (!downloadResult) return

      //* ファイルダウンロード処理
      const { blob, type } = downloadResult
      const fileBlob = new Blob(blob, { type })
      fileDownload(fileBlob, generatedFileName, type)
    }).finally(() => {
      if (tempExportType === ExportType.CAD2D) {
        setIsExporting2D(false)
      }
      if (tempExportType === ExportType.CAD3D) {
        setIsExporting3D(false)
      }
      reset()
    })
  }

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <>
      <Menu.Item value="cad-export" asChild>
        <Button onClick={onOpen} loading={isExporting} data-testid="cad-export-button" loadingText="CAD出力中...">
          <Span className="icon-wrapper">
            <Icon as={DownloadIcon} boxSize="1.2rem" />
          </Span>
          <Span>CAD出力</Span>
        </Button>
      </Menu.Item>

      <Dialog.Root open={open} onOpenChange={(e) => !e.open && handleClose()} size="lg">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md">
                <HStack>
                  <SettingIcon size="1.5rem" />
                  <Dialog.Title>CAD出力設定</Dialog.Title>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <CloseButton />
              </Dialog.CloseTrigger>
              <Dialog.Body ref={modalBodyRef} position="relative">
                <Text>CAD出力の設定を選択してください</Text>

                {isExporting2D ? (
                  <Alert.Root status="info" mt={2} mb={2} px={2} py={1} borderRadius={5}>
                    <Alert.Indicator asChild mr={2} boxSize={4}>
                      <Spinner />
                    </Alert.Indicator>
                    <Alert.Title>2D CADファイル生成中.. {progress2D}%</Alert.Title>
                  </Alert.Root>
                ) : null}

                {isExporting3D ? (
                  <Alert.Root status="info" mt={2} mb={2} px={2} py={1} borderRadius={5}>
                    <Alert.Indicator asChild mr={2} boxSize={4}>
                      <Spinner />
                    </Alert.Indicator>
                    <Alert.Title>3D CADファイル生成中.. {progress3D}%</Alert.Title>
                  </Alert.Root>
                ) : null}

                <VStack p={5} pb={0} gap={4}>
                  <HStack w="100%">
                    <Text p={0} m={0} color="black" flex={1}>
                      出力次元
                    </Text>
                    <Flex width="180px" borderWidth={1} borderColor="gray2.200" borderRadius={6} overflow="hidden">
                      <Flex {...dimensionSlotProps}>
                        <Tooltip
                          label={cad3dError ? <Text color="orange.400">{cad3dError}</Text> : undefined}
                          showArrow
                          positioning={{ placement: 'top' }}
                        >
                          <Button
                            unstyled
                            {...dimensionButtonStyle}
                            disabled={!!cad3dError}
                            loading={isExporting3D}
                            loadingText="3D"
                            backgroundImage="linear-gradient(to right, {colors.primary.500} 50%, #fff 50%)"
                            backgroundPosition={exportType === ExportType.CAD3D ? '200%' : '100%'}
                            color={exportType === ExportType.CAD3D ? 'white' : 'gray2.500'}
                            onClick={() => setExportType(ExportType.CAD3D)}
                          >
                            <CAD3DIcon />
                            3D
                          </Button>
                        </Tooltip>
                      </Flex>
                      <Flex {...dimensionSlotProps}>
                        <Tooltip
                          label={cad2dError ? <Text color="orange.400">{cad2dError}</Text> : undefined}
                          showArrow
                          positioning={{ placement: 'top' }}
                        >
                          <Button
                            unstyled
                            {...dimensionButtonStyle}
                            disabled={!!cad2dError}
                            loading={isExporting2D}
                            loadingText="2D"
                            backgroundImage="linear-gradient(to right, #fff 50%, {colors.primary.500} 50%)"
                            backgroundPosition={exportType === ExportType.CAD2D ? '100%' : '200%'}
                            color={exportType === ExportType.CAD2D ? 'white' : 'gray2.500'}
                            onClick={() => {
                              setExportType(ExportType.CAD2D)
                            }}
                          >
                            <CAD2DIcon />
                            2D
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </HStack>

                  {exportType === ExportType.CAD3D ? (
                    <HStack w="100%">
                      <Text p={0} m={0} color="black" flex={1}>
                        ファイル形式
                      </Text>
                      <Box width="180px">
                        {/*
                          A Select, not a Menu: this dialog is rendered inside the header's
                          hamburger menu, so a Menu here counts as its submenu and is forced
                          to open beside its trigger instead of under it.
                        */}
                        <Select.Root
                          collection={cadFileTypeOptions}
                          value={[extension]}
                          onValueChange={(e) => setExtension(e.value[0] as CADFileType)}
                        >
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Portal>
                            <Select.Positioner>
                              <Select.Content>
                                {cadFileTypeOptions.items.map((option) => (
                                  <Select.Item key={option.value} item={option}>
                                    {option.label}
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Portal>
                        </Select.Root>
                      </Box>
                    </HStack>
                  ) : null}

                  <HStack w="100%" alignItems="start">
                    <Box p={0} m={0} color="black" flex={1}>
                      <Text>位置合わせされたエリア</Text>
                      {connectedAreas.map((ar) => (
                        <Text
                          key={`cad-export-connected-area-${ar.current.area.inspection_area_id}`}
                          {...connectedAreaLabelStyle}
                        >
                          <PCDAlignmentIcon /> {ar.current.area.inspection_area_name}
                        </Text>
                      ))}
                    </Box>
                    <HStack width="180px">
                      <Switch.Root
                        colorPalette="blue"
                        checked={includeConnectedAreas}
                        onCheckedChange={(e) => setIncludeConnectedAreas(e.checked)}
                        size="lg"
                      >
                        <Switch.HiddenInput />
                        <Switch.Control />
                      </Switch.Root>
                      <Text color="black" fontSize="md">
                        含める
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button variant="outline" onClick={onClose}>
                  キャンセル
                </Button>
                <Spacer />
                <Button disabled={!!cad3dError && !!cad2dError} colorPalette="blue" onClick={download}>
                  ファイル生成
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}

export default CADExportButton
